// src/context/LoginContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiRequest } from '../utils/api';  // Importing the apiRequest function

// Create Login Context
const LoginContext = createContext();

// Custom hook to use Login Context
export const useLoginContext = () => useContext(LoginContext);

// Login Context Provider
export const LoginProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const navigate = useNavigate();
    const location = useLocation(); // Track current location

    // On initial load, check if the user is authenticated via API request
    useEffect(() => {
        const verifySession = async () => {
            try {
                const response = await apiRequest('/api/auth/verify-session', {
                    method: 'GET',
                    credentials: 'include', 
                });
                if (response.ok) {
                    const data = await response.json();
                    setIsAuthenticated(true);
                    setUser(data.user);  // Assuming user data is returned by this endpoint
                    if (location.pathname !== '/login') {
                        navigate(location.pathname); // Redirect to current location after login
                    }
                } else {
                    setIsAuthenticated(false);
                    setUser(null);
                }
            } catch (error) {
                console.error('Error verifying session:', error);
                setIsAuthenticated(false);
                setUser(null);
            }
        };

        verifySession();  // Call the function on mount
    }, []);

    // Handle login
    const login = (userData) => {
        setIsAuthenticated(true);
        setUser(userData);
        navigate('/');
    };

    // Handle logout
    const logout = async () => {
        try {
            const response = await apiRequest('/api/auth/logout', {
                method: 'POST',  // Assuming there's a logout endpoint
            });
            if (response.ok) {
                console.log('Logout success');
            } else {
                console.error('Logout failed');
            }
        } catch (error) {
            console.error('Error logging out:', error);
        } finally {
            setIsAuthenticated(false);
            setUser(null);
            navigate('/login');
        }
    };

    return (
        <LoginContext.Provider value={{ isAuthenticated, user, login, logout }}>
            {children}
        </LoginContext.Provider>
    );
};
